import { BASE_API_URL, IS_DEV_ENV } from 'consts/appConsts';
import {
  CHIME_APPLICATION_TOKEN,
  CHIME_DEVICE_ID,
  CHIME_DEVICE_INTELLIGENCE,
  CHIME_SESSION_TOKEN,
  CSRF_TOKEN,
} from 'consts/storageConsts';

import getRequestSignature from 'utils/encryptionHelper';
import { IS_EMBEDDED } from 'utils/queryParamHelpers';
import cookies from 'utils/storage/cookies';
import { getValueFromStorage } from 'utils/storage/storageHelper';

import retryFetch from './retryFetch';

const RETRY_DELAY = 2000;
const RETRIES = 3;

async function getRequestOptions({
  url,
  requestType,
  options,
  customHeaders,
  isDifferentOrigin,
  extraHeaders,
}) {
  const requestBody = options && options.body;
  const requestSignature = await getRequestSignature(requestBody);
  const isRequestToDifferentOrigin =
    IS_DEV_ENV || url.indexOf(BASE_API_URL) < 0 || isDifferentOrigin;

  /** The MAS flow will no longer use Rails default session cookies.
   * It will now pass the session key through a custom header,
   * and expect the key in the request headers when hitting
   * authenticated endpoints. **/
  const chimeSessionKey = getValueFromStorage(CHIME_SESSION_TOKEN);
  const chimeApplicationToken = getValueFromStorage(CHIME_APPLICATION_TOKEN);
  const deviceId = getValueFromStorage(CHIME_DEVICE_ID);
  const deviceIntelligence = getValueFromStorage(CHIME_DEVICE_INTELLIGENCE);

  const headers = customHeaders || {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${window.Chime.a}`,
    'X-Signature': requestSignature,
    'Chime-Enrollment-Embedded': IS_EMBEDDED,
    'Chime-Session': chimeSessionKey,
    'Chime-Device-UUID': deviceId,
    'Chime-Application-Token': chimeApplicationToken,
    ...(deviceIntelligence && {
      'Chime-Device-Intelligence': deviceIntelligence,
    }),
    ...extraHeaders,
  };

  const requestOptions = {
    method: requestType || 'GET',
    credentials: isRequestToDifferentOrigin ? 'include' : 'same-origin',
    mode: isRequestToDifferentOrigin ? 'cors' : 'same-origin',
    headers,
    ...options,
  };

  // Need to pass an xsrf token in request for rails to validate form submission
  // as if a form was rendered by rails.
  // This prevents rails from invalidating the current session
  const storedXsrf = cookies.get(CSRF_TOKEN);

  if (storedXsrf) {
    requestOptions.headers[CSRF_TOKEN] = storedXsrf;
  }

  return requestOptions;
}

export default async function fetchWrapper({
  url,
  requestType = 'GET',
  options,
  customHeaders,
  isDifferentOrigin,
  extraHeaders,
}) {
  const requestOptions = await getRequestOptions({
    url,
    requestType,
    options,
    customHeaders,
    isDifferentOrigin,
    extraHeaders,
  });

  if (options && options.retry) {
    return retryFetch(url, {
      retries: RETRIES,
      retryDelay: RETRY_DELAY,
      ...requestOptions,
      ...options,
    });
  }
  return fetch(url, {
    ...requestOptions,
    ...options,
  });
}
